<script lang="ts">
  import { defineComponent, PropType } from 'vue'
  import { CommonErrorI } from '@/typescript/types/abstracts/errors/ErrorHandlerTypes'

  export default defineComponent({
    name: 'TextareaItem',
    props: {
      name: { type: String, default: '' },
      error: { type: [Object, String] as PropType<CommonErrorI | string | null | undefined>, default: () => ({}) },
      label: { type: String, default: '' },
      placeholder: { type: String, default: '' }, // не підставляємо label у placeholder
      modelValue: { type: [String, Number], default: null },
      disabled: { type: Boolean, default: false },
      autocomplete: { type: String, default: '' },
      isAlwaysShowLabel: { type: Boolean, default: false },
      minHeight: { type: String, default: '100px' },
      labelExistButNotVisible: { type: Boolean, default: false },
    },
    emits: ['clearErrors', 'update:modelValue'],
    data() {
      return {
        labelIsVisible: false,
        uidFallback: `textarea-${Math.random().toString(36).slice(2)}`, // стабільний id, якщо name порожній
      }
    },
    computed: {
      internalValue: {
        get() {
          return this.modelValue
        },
        set(v: any) {
          this.$emit('update:modelValue', v)
        },
      },

      // a11y ids
      fieldId(): string {
        return this.name || this.uidFallback
      },
      hasError(): boolean {
        const e: any = this.error
        return !!(e && (e.hasError || e.message || Object.keys(e).length))
      },
      errorId(): string {
        return `${this.fieldId}-error`
      },
      describedBy(): string | undefined {
        // тільки помилка (бо hint видалили)
        return this.hasError ? this.errorId : undefined
      },
    },
    mounted() {
      if (this.isAlwaysShowLabel) this.labelIsVisible = true
    },
    methods: {
      toggleLabel(val: boolean) {
        if (!this.isAlwaysShowLabel) this.labelIsVisible = val
      },
    },
  })
</script>
<template>
  <label
    :for="fieldId"
    class="bh-label"
    :class="{ error: hasError }"
    :style="{ paddingTop: label && !labelExistButNotVisible ? '20px' : '0' }"
  >
    <Transition>
      <!-- лейбл завжди в a11y-дереві; приховуємо візуально через .sr-only -->
      <span v-if="label" class="bh-label-tip" :class="{ 'sr-only': !labelIsVisible || labelExistButNotVisible }">
        {{ label }}
      </span>
    </Transition>

    <textarea
      :id="fieldId"
      v-model="internalValue"
      name="content"
      :placeholder="placeholder"
      :autocomplete="autocomplete || undefined"
      :disabled="disabled"
      :aria-invalid="hasError ? 'true' : undefined"
      :aria-errormessage="hasError ? errorId : undefined"
      :aria-describedby="describedBy"
      :class="{ error: hasError }"
      :style="{ 'min-height': minHeight }"
      @click="$emit('clearErrors')"
      @focus="toggleLabel(true)"
      @blur="toggleLabel(false)"
    />
    <Transition>
      <HasError v-if="hasError" :key="`hasError-${fieldId}`" :error="error" :field-id="fieldId" />
    </Transition>
  </label>
</template>

<style scoped lang="scss">
  textarea {
    border: 1px solid var(--color-text-grey);
    background-color: var(--color-background-grey-1);
    border-radius: 6px;
    color: var(--color-text-4);
    padding: 13px 10px;
    height: 43px;
    box-sizing: border-box;
    position: relative;
    z-index: 3;
    width: 100%;
    transition: all 0.3s ease;
    resize: none;
    font-family: var(--font-source-sans);

    &::placeholder {
      color: var(--color-placeholder-grey);
      opacity: 1 !important;
      font-size: 1em;
      height: 1.2em;
      line-height: 1.2em;
      font-weight: 400;
      transition: color 0.2s ease;
    }

    &:focus,
    &:focus-within,
    &:focus-visible {
      outline: none;
      border-color: var(--color-link-blue);
      background-color: var(--color-background-input);

      &::-webkit-input-placeholder {
        color: transparent;
      }
    }
    &[disabled] {
      background-color: var(--color-background-grey-1);
    }
    &.error {
      border-color: var(--color-border-error);
      background-color: var(--color-background-error);
    }
  }

  .bh-label {
    position: relative;
    display: block;
  }
  .bh-label-tip {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    font-size: 12px;
  }
  .sr-only {
    position: absolute !important;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    border: 0;
    white-space: nowrap;
  }
</style>
